import { Link, Navigate } from 'react-router-dom'
import { exportApi } from '../../api/exports'
import { BbIcon } from '../../components/BbIcon'
import { BbStepper } from '../../components/BbStepper'
import { useCompanies } from '../../companies/useCompanies'
import { DATE_PRESETS, useExportWizard } from '../../export/ExportWizardContext'
import { useAsync } from '../../hooks/useAsync'
import { formatInt } from '../../lib/format'
import { EXPORT_STEPS } from './steps'

/* Step 2, ported from "batchbooks_export_step_2_updated".

   The filter box is real: it maps to the field the backend actually filters on
   (DisplayName for name-list entities, DocNumber for transactions), which the
   preview endpoint reports as `referenceField`. The matching-record count below
   comes from the same preview call, so the user sees how many records would be
   exported before committing. */

/** Human label for the reference field the backend filters on. */
function referenceLabel(field: string): string {
  switch (field) {
    case 'DisplayName':
      return 'Name'
    case 'DocNumber':
      return 'Doc Number'
    default:
      return field
  }
}

export function ExportFiltersPage() {
  const wizard = useExportWizard()
  const { current } = useCompanies()

  const preview = useAsync(
    () => {
      const companyId = current?.id
      if (!companyId || !wizard.entityType) return Promise.resolve(null)
      return exportApi.preview({
        qboCompanyId: companyId,
        entityType: wizard.entityType,
        reference: wizard.reference || undefined,
        dateMode: wizard.dateMode,
        preset: wizard.dateMode === 'preset' ? wizard.preset : undefined,
        from: wizard.dateMode === 'custom' ? wizard.from || undefined : undefined,
        to: wizard.dateMode === 'custom' ? wizard.to || undefined : undefined,
        page: 1,
        pageSize: 1,
      })
    },
    [
      current?.id,
      wizard.entityType,
      wizard.reference,
      wizard.dateMode,
      wizard.preset,
      wizard.from,
      wizard.to,
    ],
  )

  // Filters are meaningless without a subject, and the wizard state is in
  // memory only — a reload or deep link arrives with nothing set.
  const refId = `ref-${wizard.entityType}`
  if (!wizard.entityType) return <Navigate to="/app/export" replace />

  const subject = wizard.entity?.label ?? wizard.entityType
  const custom = wizard.dateMode === 'custom'
  const label = referenceLabel(preview.data?.referenceField ?? 'Reference')
  const count = preview.data?.totalCount

  return (
    <div className="bb bb-wizard">
      <BbStepper steps={EXPORT_STEPS} current={1} />

      <h2>Select Filters for {subject}</h2>
      <p className="bb-map-help">Please specify filters for QuickBooks Export</p>

      <div className="bb-filters">
        <div className="bb-filter-row">
          <label className="bb-filter-k" htmlFor={refId}>
            {label} :
          </label>
          <input
            className="bb-input"
            id={refId}
            value={wizard.reference}
            placeholder={label}
            onChange={(event) => wizard.setReference(event.target.value)}
          />
        </div>

        <h3 className="bb-sub-h">Date</h3>

        <div className="bb-filter-row">
          <label className="bb-radio">
            <input
              type="radio"
              name="date_mode"
              value="preset"
              checked={!custom}
              onChange={() => wizard.setDateMode('preset')}
            />
            Preset Date :
          </label>
          <select
            className="bb-select"
            aria-label="Preset date range"
            disabled={custom}
            value={wizard.preset}
            onChange={(event) => wizard.setPreset(event.target.value)}
          >
            {DATE_PRESETS.map((preset) => (
              <option value={preset} key={preset}>
                {preset}
              </option>
            ))}
          </select>
        </div>

        <div className="bb-filter-row">
          <label className="bb-radio">
            <input
              type="radio"
              name="date_mode"
              value="custom"
              checked={custom}
              onChange={() => wizard.setDateMode('custom')}
            />
            Customized Date :
          </label>
          <span className="bb-dates">
            <input
              className="bb-input"
              type="date"
              aria-label="Start date"
              disabled={!custom}
              value={wizard.from}
              onChange={(event) => wizard.setFrom(event.target.value)}
            />
            <button
              className="bb-clear"
              type="button"
              disabled={!custom || !wizard.from}
              onClick={() => wizard.setFrom('')}
            >
              clear
            </button>
            <input
              className="bb-input"
              type="date"
              aria-label="End date"
              disabled={!custom}
              value={wizard.to}
              onChange={(event) => wizard.setTo(event.target.value)}
            />
            <button
              className="bb-clear"
              type="button"
              disabled={!custom || !wizard.to}
              onClick={() => wizard.setTo('')}
            >
              clear
            </button>
          </span>
        </div>

        <div className="bb-filter-row bb-filter-count">
          {preview.loading ? (
            <span className="bb-map-help">Checking QuickBooks…</span>
          ) : preview.error ? (
            <span className="bb-map-help" style={{ color: 'var(--bb-red)' }}>
              Could not reach QuickBooks for a count.
            </span>
          ) : count == null ? (
            <span className="bb-map-help">This entity does not report a total.</span>
          ) : (
            <span className="bb-map-help">
              {formatInt(count)} matching record{count === 1 ? '' : 's'} in QuickBooks
            </span>
          )}
        </div>
      </div>

      <div className="bb-wizard-foot">
        <Link className="bb-btn bb-btn-quiet" to="/app/export">
          <BbIcon name="arrow-left" />
          Previous
        </Link>
        <span className="bb-wizard-foot-r">
          <Link className="bb-btn bb-btn-green" to="/app/export/fields">
            Next
            <BbIcon name="arrow-right" />
          </Link>
        </span>
      </div>
    </div>
  )
}
